import { useEffect, useRef } from "react";
import { sendBrowserNotification, getNotificationPermission } from "@utils/notification";
import { t } from "i18next";

/**
 * 监听视频生成状态变化，并在完成时发送浏览器通知
 */
export const useVideoGenerationNotification = (videoList: any[] = []) => {
  // 使用 ref 存储「上一轮」每个视频的状态，用于检测 processing/queued -> completed 的跃迁
  const previousListRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (getNotificationPermission() !== "granted") {
      return;
    }

    videoList.forEach((video: any) => {
      if (video == null) return;
      // 统一成 string，避免 id 有时为数字有时为字符串导致 Map 里存两份
      const videoId = video.id != null ? String(video.id) : null;
      if (videoId == null) return;

      const currentStatus = video.jobItem?.status;
      const previousStatus = previousListRef.current.get(videoId);

      if ((previousStatus === "processing" || previousStatus === "queued") && currentStatus === "completed") {
        sendVideoCompletedNotification(video);
      }

      previousListRef.current.set(videoId, currentStatus);
    });

    // 只保留当前列表里仍存在的 id，避免 ref 无限增长
    const currentVideoIds = new Set(
      videoList.filter((v: any) => v != null && v.id != null).map((v: any) => String(v.id)),
    );
    previousListRef.current.forEach((_, videoId) => {
      if (!currentVideoIds.has(videoId)) {
        previousListRef.current.delete(videoId);
      }
    });
  }, [videoList]);
};

/**
 * 发送视频生成完成的通知
 * 与 sendBrowserNotification 默认行为一致：requireInteraction 保持为 true，便于在 Mac 上看到
 */
export const sendVideoCompletedNotification = (video: any) => {
  try {
    const body = t("Click to view");
    const icon = video.jobItem?.context?.coverImageUrl || "https://staticjs.kaloclip.com/sources/images/favicon.png";
    let notification = sendBrowserNotification(t("Video is ready!") + " 🎉", {
      body,
      icon,
      tag: `video-${video.id}`,
    });
    if (!notification) {
      notification = sendBrowserNotification(t("Video is ready!") + " 🎉", {
        body,
        icon,
        tag: `video-${video.id}`,
      });
    }

    if (notification) {
      notification.onclick = () => {
        window.focus();
        if (window.location.pathname !== "/library") {
          window.location.href = "/library?type=video";
        }
        notification!.close();
      };
      console.log("发送通知成功");
    } else {
      console.log("发送通知失败");
    }
  } catch (error) {
    console.error("发送通知失败", error);
  }
};
